// Time and date helpers. Pure functions, no DOM.

/** Format minutes-since-midnight as HH:MM. */
export function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Parse HH:MM into minutes since midnight. Returns NaN on invalid. */
export function parseTime(hhmm: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) return NaN;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return NaN;
  return h * 60 + m;
}

/** ISO yyyy-mm-dd in local time from a Date. */
export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse yyyy-mm-dd as a local Date at 00:00. */
export function fromISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Number of days between two ISO dates, inclusive of both ends. */
export function daysBetween(startIso: string, endIso: string): number {
  const start = fromISODate(startIso).getTime();
  const end = fromISODate(endIso).getTime();
  return Math.round((end - start) / 86_400_000) + 1;
}

/** Enumerate all ISO dates in [start, end], filtered by weekday mask. */
export function enumerateDates(startIso: string, endIso: string, allowedWeekdays: number[]): string[] {
  const allowed = new Set(allowedWeekdays);
  const result: string[] = [];
  const start = fromISODate(startIso);
  const end = fromISODate(endIso);
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    if (allowed.has(cursor.getDay())) {
      result.push(toISODate(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
}

/** German weekday labels, indexed 0..6 where 0 = Sunday (matches Date.getDay). */
export const WEEKDAY_LABELS_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'] as const;
export const WEEKDAY_LABELS_LONG_DE = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
] as const;

/** Format ISO date as "Mo, 15.04." */
export function formatDateShort(iso: string): string {
  const d = fromISODate(iso);
  const wd = WEEKDAY_LABELS_DE[d.getDay()];
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${wd}, ${day}.${month}.`;
}
